import React, { useEffect } from "react";
import { Layout, Header, Footer, Tab, MinFooter } from "@kidslivesafe/sc-react";
import { useAppSelector } from "../../hooks/hooks";
import { selectBrandInfo } from "../../features/brand/brandSlice";
import { useAppDispatch } from "../../hooks/hooks";
import { useNavigate } from "react-router-dom";
import {
  selectIsOpen,
  toggleOpen,
  setTab,
  closeMultiSearch,
  selectTab,
} from "../../features/multisearch/multisearchSlice";

import {
  AccountType,
  logoutAsync,
  selectAccountType,
  selectIdProtectData,
  selectIsPIS75Variant,
} from "../../features/authentication/authenticationSlice";
import gateKeeper from "../../components/HigherOrderComponents/gatekeeper";

import Logo from "@assets/images/brand-logo.svg";
import HeaderLogo from "@assets/images/brand-logo--header.svg";
import FooterLogo from "@assets/images/brand-footer-logo.svg";

import facebook from "../../assets/global/images/social_facebook.svg";
import twitter from "../../assets/global/images/social_twitter.svg";
import instagram from "../../assets/global/images/social_instagram.svg";
import youtube from "../../assets/global/images/social_youtube.svg";
import linkedin from "../../assets/global/images/social_linkedin.svg";
import tiktok from "../../assets/global/images/social_tiktok.svg";
import { useMultiSearchProps } from "../../hooks/multiSearchPropsHook";
import flexAccessLayout from "../../components/HigherOrderComponents/flexAccessLayout";
import brandedLoginLayout from "../../components/HigherOrderComponents/brandedLoginLayout";
import deepLinkRedirect from "../../components/HigherOrderComponents/deepLinkRedirect";
import variantAccess from "../../components/HigherOrderComponents/variantAccess";
import { sendMemberEventAsync } from "../../features/admin/adminSlice";
import { useFooterProps } from "../../hooks/footerPropsHook";
import {
  DeactivateBanner,
  ReactivateBanner,
} from "../../components/UIComponents/ReactivateBanner";

const socialAssetMap = {
  facebook: facebook,
  twitter: twitter,
  instagram: instagram,
  youtube: youtube,
  linkedin: linkedin,
  tiktok: tiktok,
};

interface PageLayoutProps {
  children: React.ReactNode;
  pageName: string;
  documentTitle?: string;
  header?: boolean;
  footerType?: "default" | "no-footer" | "min-footer";
  minFooterOptions?: {
    color?: "base" | "white";
  };
  noFooter?: boolean;
  displayButtons?: boolean;
  includeReactivateBanner?: boolean;
  includeDeactivateBanner?: boolean;
}

const PageLayout: React.FC<PageLayoutProps> = ({
  pageName,
  children,
  documentTitle,
  header = false,
  displayButtons = false,
  noFooter = false,
  minFooterOptions = { color: "base" },
  footerType = "default",
  includeReactivateBanner = true,
  includeDeactivateBanner = true,
}) => {
  const [tabs, setTabs] = React.useState<Tab[]>([
    "people",
    "property",
    "phone",
    "social",
    "foreclosure",
  ]);
  const [multiSearchFooter, setMultiSearchFooter] =
    React.useState<boolean>(true);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const accountType = useAppSelector(selectAccountType);
  const idProtectData = useAppSelector(selectIdProtectData);
  const isPIS75Variant = useAppSelector(selectIsPIS75Variant);
  const brandInfo = useAppSelector(selectBrandInfo);

  const handleLogout = () => {
    dispatch(closeMultiSearch());
    dispatch(logoutAsync({ location: "navLogout" }));
    navigate("/logout");
  };

  const handleIdProtectLink = () => {
    dispatch(
      sendMemberEventAsync({
        EventCategory: "Menu",
        EventAction: "ID Protect Express",
      }),
    );
    navigate("/id-protect");
    // Scroll to the top of the page after navigation
    window.scrollTo(0, 0);
  };

  const linkBank = [
    {
      text: "Dashboard",
      link: "/",
      target: "_self",
    },
    {
      text: "Activity History",
      link: "/activity-history",
      target: "_self",
    },
    {
      text: "ID Protect",
      onClick: handleIdProtectLink,
    },
    {
      text: "Profile Protection",
      link: "/protection/status",
      target: "_self",
    },
    {
      text: "Lists",
      link: "/lists",
      target: "_self",
    },
    {
      text: "Neighborhood Watch",
      link: "/neighborhood-home",
      target: "_self",
    },
    {
      text: "People",
      link: "/people-home",
      target: "_self",
    },
    {
      text: "Phone",
      link: "/phone-home",
      target: "_self",
    },
    {
      text: "Property",
      link: "/property-home?propSearch=property",
      target: "_self",
    },
    {
      text: "Foreclosure",
      link: "/property-home?propSearch=foreclosure",
      target: "_self",
    },
    {
      text: "Social",
      link: "/social-home",
      target: "_self",
    },
    {
      text: "Vehicle",
      link: "/vehicle-home",
      target: "_self",
    },
    {
      text: "My Account",
      link: "/my-account",
      target: "_self",
    },
    {
      text: "Contact Us",
      link: `${brandInfo.homeUrl}/help-center/contact`,
      target: "_blank",
    },
    {
      text: "Logout",
      onClick: handleLogout,
    },
  ];

  const renderLinks = (type: AccountType | null) => {
    let allowedLinks = [] as string[];
    switch (type) {
      case "aamva":
        allowedLinks = ["Dashboard", "Activity History", "Logout"];
        break;
      case "property":
        if (brandInfo.acronym === "pdc") {
          allowedLinks = [
            "Dashboard",
            "Activity History",
            "Neighborhood Watch",
            "People",
            "Phone",
            "Property",
            "Foreclosure",
            "Vehicle",
            "Social",
            "Lists",
            "ID Protect",
            "Profile Protection",
            "My Account",
            "Contact Us",
            "Logout",
          ];
        } else if (brandInfo.acronym === "pdc") {
          allowedLinks = [
            "Dashboard",
            "Activity History",
            "Neighborhood Watch",
            "People",
            "Phone",
            "Property",
            "Foreclosure",
            "Vehicle",
            "Social",
            "ID Protect",
            "Profile Protection",
            "My Account",
            "Contact Us",
            "Logout",
          ];
        } else {
          allowedLinks = [
            "Dashboard",
            "Activity History",
            "Lists",
            "Neighborhood Watch",
            "People",
            "Phone",
            "Property",
            "Foreclosure",
            "Social",
            "ID Protect",
            "Profile Protection",
            "My Account",
            "Contact Us",
            "Logout",
          ];
        }
        break;
      default:
        if (brandInfo.acronym === "pdc") {
          allowedLinks = [
            "Dashboard",
            "Activity History",
            "Neighborhood Watch",
            "People",
            "Phone",
            "Property",
            "Foreclosure",
            "Vehicle",
            "Social",
            "Lists",
            "ID Protect",
            "Profile Protection",
            "My Account",
            "Contact Us",
            "Logout",
          ];
        } else {
          allowedLinks = [
            "Dashboard",
            "Activity History",
            "Lists",
            "Neighborhood Watch",
            "People",
            "Phone",
            "Property",
            "Foreclosure",
            "Social",
            "ID Protect",
            "Profile Protection",
            "My Account",
            "Contact Us",
            "Logout",
          ];
        }
    }
    return (
      linkBank
        .filter(link => {
          if (link.text === "ID Protect") {
            return (
              idProtectData.isIdProtectAvailable ||
              idProtectData.idProtectMember
            );
          }
          if (link.text === "Profile Protection") {
            return isPIS75Variant;
          }
          return allowedLinks.includes(link.text);
        })
        // sorts by order of appearance in allowedLinks to be able to dynamically control
        .sort((a, b) => {
          return allowedLinks.indexOf(a.text) - allowedLinks.indexOf(b.text);
        })
    );
  };

  useEffect(() => {
    const whichTabs = (type: AccountType | null) => {
      switch (type) {
        case "aamva":
          setTabs(["vehicle"]);
          setMultiSearchFooter(false);
          dispatch(setTab("vehicle"));
          break; //aamva truncated view
        case "neighborhood":
          setTabs(["neighborhood", "people", "property", "phone"]);
          setMultiSearchFooter(true);
          dispatch(setTab("neighborhood"));
          break;
        case "people":
          if (brandInfo.acronym === "pdc") {
            setTabs(["people", "property", "phone", "vehicle"]);
            setMultiSearchFooter(true);
            dispatch(setTab("people"));
          } else if (brandInfo.acronym === "kls") {
            setTabs(["people", "property", "phone", "social"]);
            setMultiSearchFooter(true);
            dispatch(setTab("people"));
          } else {
            setTabs(["people", "property", "phone", "foreclosure"]);
            setMultiSearchFooter(true);
            dispatch(setTab("people"));
          }
          break;
        case "phone":
          if (brandInfo.acronym === "pdc") {
            setTabs(["phone", "people", "property", "vehicle"]);
            setMultiSearchFooter(true);
            dispatch(setTab("phone"));
          } else if (brandInfo.acronym === "kls") {
            setTabs(["phone", "people", "property", "social"]);
            setMultiSearchFooter(true);
            dispatch(setTab("phone"));
          } else {
            setTabs(["phone", "people", "property", "foreclosure"]);
            setMultiSearchFooter(true);
            dispatch(setTab("phone"));
          }
          break;
        case "property":
          if (brandInfo.acronym === "pdc") {
            setTabs(["property", "people", "phone", "vehicle"]);
            setMultiSearchFooter(true);
            dispatch(setTab("property"));
          } else if (brandInfo.acronym === "kls") {
            setTabs(["property", "people", "phone", "social"]);
            setMultiSearchFooter(true);
            dispatch(setTab("property"));
          } else if (brandInfo.acronym === "pf") {
            setTabs(["property", "foreclosure", "people", "phone"]);
            setMultiSearchFooter(true);
            dispatch(setTab("property"));
          } else {
            setTabs(["property", "people", "phone", "foreclosure"]);
            setMultiSearchFooter(true);
            dispatch(setTab("property"));
          }
          break;
        case "vehicle":
          setTabs(["vehicle", "people", "property", "phone"]);
          setMultiSearchFooter(true);
          dispatch(setTab("vehicle"));
          break;
        // no social funnel so no social accountType
        default: // defaults to "people" accountType
          if (brandInfo.acronym === "pdc") {
            setTabs(["property", "people", "phone", "vehicle"]);
            setMultiSearchFooter(true);
            dispatch(setTab("property"));
          } else if (brandInfo.acronym === "kls") {
            setTabs(["people", "property", "phone", "social"]);
            setMultiSearchFooter(true);
            dispatch(setTab("people"));
          } else {
            setTabs(["people", "property", "phone", "foreclosure"]);
            setMultiSearchFooter(true);
            dispatch(setTab("people"));
            break;
          }
      }
    };

    whichTabs(accountType);
  }, [accountType, dispatch, brandInfo]);

  const multiSearchProps = useMultiSearchProps(
    {
      handleCancelSearchMiddleware: () => {
        dispatch(toggleOpen());
      },
      handlePeopleSearchMiddleware: () => dispatch(toggleOpen()),
      handleSocialSearchMiddleware: () => dispatch(toggleOpen()),
      handlePhoneSearchMiddleware: () => dispatch(toggleOpen()),
      handlePropertySearchMiddleware: () => dispatch(toggleOpen()),
      handleVehicleSearchMiddleware: () => dispatch(toggleOpen()),
      handleNeighborhoodSearchMiddleware: () => dispatch(toggleOpen()),
      handleNeighborhoodNameSearchMiddleware: () => dispatch(toggleOpen()),
      openTab: useAppSelector(selectTab),
      handleSetTab: (tab: Tab | undefined) => {
        dispatch(setTab(tab));
      },
      resetInput: true,
      tabs: tabs,
      footer: multiSearchFooter,
      backgroundStyle: "alt",
      vehicleModifactions: {
        showVehicleRadioInput: true,
      },
      propertyModifactions: {
        showPropertyRadioInput:
          brandInfo.acronym === "pdc" || brandInfo.acronym === "kls"
            ? true
            : false,
      },
    },
    "",
  );

  const multiSearchOpen = useAppSelector(selectIsOpen);

  useEffect(() => {
    document.title = documentTitle
      ? `${documentTitle} | ${brandInfo.name}`
      : `${pageName} | ${brandInfo.name}`;
  }, [documentTitle, brandInfo.name, pageName]);

  // Close menu when navigating to a new page
  useEffect(() => {
    dispatch(closeMultiSearch());
  }, [pageName, dispatch]);

  const menuButtonLinks = renderLinks(accountType);

  const footerProps = useFooterProps();

  brandInfo.footerSocials.forEach(social => {
    const socialObj = {
      name: social,
      assetUrl: socialAssetMap[social],
      link: brandInfo.socialUrls[social] || "",
      target: "_blank",
    };
    footerProps.socialMediaLinks.push(socialObj);
  });

  return (
    <>
      <div className={`page page-${pageName}`}>
        {header ? (
          <>
            <Header
              menuButtonLinks={menuButtonLinks}
              displayButtons={displayButtons}
              logoUrl={HeaderLogo}
              menuLogoUrl={brandInfo.acronym === "qpr" ? HeaderLogo : Logo}
              homeUrl="/"
              expand={multiSearchOpen}
              setExpand={multiSearchProps.handleCancelSearch}
              changeBackgroundOnExpand={
                brandInfo.acronym === "pf" ? true : false
              }
              multiSearchOptions={multiSearchProps}
            />
            {children}
          </>
        ) : (
          <Layout
            type="flex"
            flexProps={{ direction: "column", justifyContent: "center" }}
          >
            {children}
          </Layout>
        )}
        {includeReactivateBanner && <ReactivateBanner />}
        {includeDeactivateBanner && <DeactivateBanner />}
      </div>
      {footerType === "default" && (
        <Footer
          {...footerProps}
          logoUrl={FooterLogo}
          disclaimerBrand={brandInfo.acronym === "pf" ? "pf" : "default"}
        />
      )}
      {footerType === "min-footer" && (
        <MinFooter
          brand={brandInfo.name}
          color={minFooterOptions.color}
          llcName={`${brandInfo.llcName}, LLC.`}
          links={[
            {
              text: "Privacy Policy",
              link: `${brandInfo.homeUrl}/help-center/privacy`,
            },
            {
              text: "Fair Credit Reporting Act",
              link: `${brandInfo.homeUrl}/help-center/fcra`,
            },
            {
              text: "Terms of Use",
              link: `${brandInfo.homeUrl}/help-center/terms`,
            },
          ]}
        />
      )}
      {footerType === "no-footer" && null}
    </>
  );
};

export default PageLayout;

export const GateKeeperPageLayout = gateKeeper(PageLayout);

export const FlexAccessLayout = flexAccessLayout(PageLayout);

export const BrandedLoginLayout = brandedLoginLayout(PageLayout);

export const DeepLinkingRedirectLayout = deepLinkRedirect(PageLayout);

export const createVariantGateKeeperPageLayout = (allowedVariants: string[]) =>
  variantAccess(allowedVariants)(gateKeeper(PageLayout));

export const PIS75GateKeeperPageLayout = createVariantGateKeeperPageLayout([
  "pis75",
]);
